import { useCallback, useEffect, useRef, useState } from "react";
import { Terminal } from "@xterm/xterm";
import { FitAddon } from "@xterm/addon-fit";
import { WebLinksAddon } from "@xterm/addon-web-links";
import "@xterm/xterm/css/xterm.css";
import { terminalUrl } from "../lib/api";
import { Icon } from "./Icon";

export type ConnectionState = "idle" | "connecting" | "open" | "retrying" | "closed" | "failed";

const MAX_ATTEMPTS = 6;
const encoder = new TextEncoder();

const LIGHT_THEME = {
  background: "#10171c",
  foreground: "#d9e3e4",
  cursor: "#a79ff2",
  cursorAccent: "#10171c",
  selectionBackground: "#2f3d52",
  black: "#10171c",
  red: "#ef8e82",
  green: "#70c49c",
  yellow: "#dca94f",
  blue: "#92b8f5",
  magenta: "#c79bf0",
  cyan: "#7fd1d0",
  white: "#d9e3e4",
  brightBlack: "#5d6b73",
  brightRed: "#ffa79b",
  brightGreen: "#8ed9ae",
  brightYellow: "#f0c069",
  brightBlue: "#aacdff",
  brightMagenta: "#d9b6ff",
  brightCyan: "#9fe5e3",
  brightWhite: "#ffffff",
};

const DARK_THEME = { ...LIGHT_THEME, background: "#080c0f", cursorAccent: "#080c0f" };

const STATE_LABEL: Record<ConnectionState, string> = {
  idle: "Not connected",
  connecting: "Connecting",
  open: "Connected",
  retrying: "Reconnecting",
  closed: "Disconnected",
  failed: "Connection failed",
};

/**
 * The sandbox terminal. Keystrokes go up as binary frames, resizes as a small JSON message, and
 * the container's output comes back as binary. A dropped socket retries with a backoff; a clean
 * close (code 1000, which is what the container sends when the shell exits) does not.
 */
export function SandboxTerminal({
  session = "main",
  cwd,
  dark,
  active,
  onStateChange,
}: {
  session?: string;
  cwd?: string;
  dark: boolean;
  /** Only connect when the sandbox is running and this pane is on screen. */
  active: boolean;
  onStateChange?: (state: ConnectionState) => void;
}) {
  const host = useRef<HTMLDivElement | null>(null);
  const term = useRef<Terminal | null>(null);
  const fit = useRef<FitAddon | null>(null);
  const socket = useRef<WebSocket | null>(null);
  const retryTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const attempts = useRef(0);
  const closedCleanly = useRef(false);
  const wanted = useRef(active);
  const [state, setState] = useState<ConnectionState>("idle");

  const report = useCallback((next: ConnectionState) => {
    setState(next);
    onStateChange?.(next);
  }, [onStateChange]);

  // ---------------------------------------------------------------- the terminal itself
  useEffect(() => {
    if (!host.current || term.current) return;
    const terminal = new Terminal({
      cursorBlink: true,
      convertEol: false,
      fontFamily:
        'ui-monospace, "SF Mono", SFMono-Regular, Menlo, "JetBrains Mono", Consolas, "Liberation Mono", monospace',
      fontSize: 13,
      lineHeight: 1.25,
      scrollback: 5000,
      allowProposedApi: true,
      theme: dark ? DARK_THEME : LIGHT_THEME,
    });
    const fitAddon = new FitAddon();
    terminal.loadAddon(fitAddon);
    terminal.loadAddon(new WebLinksAddon());
    terminal.open(host.current);
    term.current = terminal;
    fit.current = fitAddon;

    const onData = terminal.onData((data) => {
      const ws = socket.current;
      if (ws && ws.readyState === WebSocket.OPEN) ws.send(encoder.encode(data));
    });
    const onBinary = terminal.onBinary((data) => {
      const ws = socket.current;
      if (ws && ws.readyState === WebSocket.OPEN) {
        const bytes = new Uint8Array(data.length);
        for (let i = 0; i < data.length; i++) bytes[i] = data.charCodeAt(i) & 255;
        ws.send(bytes);
      }
    });
    const onResize = terminal.onResize(({ cols, rows }) => {
      const ws = socket.current;
      if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ cols, rows }));
    });

    return () => {
      onData.dispose();
      onBinary.dispose();
      onResize.dispose();
      terminal.dispose();
      term.current = null;
      fit.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const terminal = term.current;
    if (terminal) terminal.options.theme = dark ? DARK_THEME : LIGHT_THEME;
  }, [dark]);

  // Fit to the pane whenever it changes size, but never to a hidden (zero width) pane.
  useEffect(() => {
    const element = host.current;
    if (!element) return;
    const refit = () => {
      if (element.clientWidth < 40 || element.clientHeight < 20) return;
      try {
        fit.current?.fit();
      } catch {
        // xterm throws if the element is detached mid-measure; the next tick will catch up.
      }
    };
    const observer = new ResizeObserver(refit);
    observer.observe(element);
    refit();
    return () => observer.disconnect();
  }, []);

  // ---------------------------------------------------------------- the socket
  const connect = useCallback(() => {
    const terminal = term.current;
    if (!terminal || !wanted.current) return;
    if (socket.current && (socket.current.readyState === WebSocket.OPEN
      || socket.current.readyState === WebSocket.CONNECTING)) return;

    try {
      fit.current?.fit();
    } catch { /* measured below from whatever xterm currently reports */ }

    report(attempts.current > 0 ? "retrying" : "connecting");
    const url = terminalUrl({
      cols: terminal.cols || 80,
      rows: terminal.rows || 24,
      session,
      cwd,
    });

    let ws: WebSocket;
    try {
      ws = new WebSocket(url);
    } catch {
      report("failed");
      return;
    }
    ws.binaryType = "arraybuffer";
    socket.current = ws;

    ws.onopen = () => {
      attempts.current = 0;
      closedCleanly.current = false;
      report("open");
      ws.send(JSON.stringify({ cols: terminal.cols, rows: terminal.rows }));
      terminal.focus();
    };

    ws.onmessage = (event: MessageEvent) => {
      if (event.data instanceof ArrayBuffer) {
        terminal.write(new Uint8Array(event.data));
      } else if (typeof event.data === "string") {
        terminal.write(event.data);
      }
    };

    ws.onclose = (event: CloseEvent) => {
      socket.current = null;
      if (event.code === 1000) {
        closedCleanly.current = true;
        terminal.writeln("\r\n\x1b[2m[the terminal session ended]\x1b[0m");
        report("closed");
        return;
      }
      if (!wanted.current) {
        report("idle");
        return;
      }
      if (attempts.current >= MAX_ATTEMPTS) {
        report("failed");
        return;
      }
      const delay = Math.min(15000, 1000 * 2 ** attempts.current);
      attempts.current += 1;
      report("retrying");
      retryTimer.current = setTimeout(connect, delay);
    };

    ws.onerror = () => {
      // `onclose` always follows, and it owns the retry decision.
    };
  }, [cwd, report, session]);

  useEffect(() => {
    wanted.current = active;
    if (active) {
      if (!socket.current && !closedCleanly.current) connect();
    } else {
      if (retryTimer.current) clearTimeout(retryTimer.current);
      socket.current?.close(1000, "left the lab");
      socket.current = null;
      report("idle");
    }
  }, [active, connect, report]);

  useEffect(() => () => {
    if (retryTimer.current) clearTimeout(retryTimer.current);
    wanted.current = false;
    socket.current?.close(1000, "closed");
    socket.current = null;
  }, []);

  const reconnect = () => {
    attempts.current = 0;
    closedCleanly.current = false;
    wanted.current = true;
    if (retryTimer.current) clearTimeout(retryTimer.current);
    connect();
  };

  const dotClass = state === "open"
    ? "dot dot-live"
    : state === "connecting" || state === "retrying"
      ? "dot dot-busy"
      : state === "failed"
        ? "dot dot-down"
        : "dot";

  return (
    <>
      <div className="pane-head">
        <Icon name="terminal" size={14} />
        <span>Terminal</span>
        <span className="spread conn">
          <span className={dotClass} aria-hidden="true" />
          <span aria-live="polite">{STATE_LABEL[state]}</span>
        </span>
        {state === "failed" || state === "closed" ? (
          <button type="button" className="btn btn-sm" onClick={reconnect}>
            Reconnect
          </button>
        ) : null}
      </div>
      <div className="pane-body">
        <div
          className="terminal-host"
          ref={host}
          role="group"
          aria-label="Sandbox terminal"
        />
      </div>
    </>
  );
}

/** Shown in place of the terminal when there is no sandbox to talk to. */
export function TerminalUnavailable({
  title,
  children,
  action,
}: {
  title: string;
  children?: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <>
      <div className="pane-head">
        <Icon name="terminal" size={14} />
        <span>Terminal</span>
        <span className="spread conn">
          <span className="dot" aria-hidden="true" />
          <span>Not connected</span>
        </span>
      </div>
      <div className="pane-body">
        <div className="terminal-offline">
          <h3>{title}</h3>
          {children ? <p>{children}</p> : null}
          {action}
        </div>
      </div>
    </>
  );
}
